import { formatRupiah } from "@/lib/display-format";
import {SimpleProfitInfo} from './simple-profit-context';
import { FinancialMappingInfo, useFinancialReporting } from './financial-reporting-context';
import { AlertTriangle, Archive, FileText, Landmark, Tags, UserRound } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { transactionBrandName, type BusinessUnit } from "@/lib/business-units";
import { categoryName, type FinanceCategory } from "@/lib/categories";
import type { Language, Transaction } from "@/lib/finance";
import { accountName, type FinancialAccount } from "@/lib/profit-loss";

type Props = {
  transaction: Transaction | null;
  onOpenChange: (open: boolean) => void;
  language: Language;
  units?: BusinessUnit[];
  categories?: FinanceCategory[];
  accounts?: FinancialAccount[];
  persisted?: boolean;
};

function money(value: number, language: Language) {
  return formatRupiah(value, language);
}

function available(value: unknown) {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

function transactionTime(transaction: Transaction) {
  if (!transaction.transactionTimestamp) return "—";
  const value = transaction.transactionTimestamp.includes("T")
    ? transaction.transactionTimestamp.split("T")[1]
    : transaction.transactionTimestamp;
  return value.slice(0, 8) || "—";
}

export function TransactionDetailPreview({
  transaction,
  onOpenChange,
  language,
  units,
  categories,
  accounts,
  persisted = true,
}: Props) {
  const id = language === "id";
  const reporting=useFinancialReporting();
  if (!transaction) return null;
  const financial = reporting ? reporting.resolve(transaction).account : accounts?.find((item) => item.slug === transaction.financialAccount);
  const sources = transaction.sourceFiles?.length ? transaction.sourceFiles.join(" · ") : transaction.sourceFile;
  const direction = transaction.direction === "CREDIT"
    ? (id ? "Kredit / pemasukan" : "Credit / income")
    : transaction.direction === "DEBIT"
      ? (id ? "Debet / pengeluaran" : "Debit / expense")
      : (id ? "Belum tersedia" : "Unavailable");

  return <Sheet open onOpenChange={onOpenChange}>
    <SheetContent className="w-full overflow-y-auto sm:max-w-xl">
      <SheetHeader className="border-b px-4 py-5 sm:px-6">
        <div className="flex flex-wrap items-center gap-2">
          <SheetTitle>{id ? (persisted ? "Detail transaksi" : "Preview transaksi") : (persisted ? "Transaction details" : "Transaction preview")}</SheetTitle>
          {!persisted && <Badge className="bg-[#a13b6e]">{id ? "Belum disimpan" : "Not saved"}</Badge>}
          {transaction.deletedAt && <Badge variant="destructive">{id ? "Terhapus" : "Deleted"}</Badge>}
        </div>
        <SheetDescription>{persisted
          ? (id ? "Tampilan lengkap transaksi yang dipilih." : "Complete view of the selected transaction.")
          : (id ? "Data ini masih berada dalam proses pemeriksaan dan belum menjadi transaksi database." : "This row is still under review and is not yet a database transaction.")}</SheetDescription>
      </SheetHeader>

      <div className="space-y-4 p-4 pb-8 sm:p-6">
        <section className={`rounded border-l-4 p-4 ${transaction.direction === "CREDIT" ? "border-l-[#258778] bg-[#258778]/8" : "border-l-[#ef334f] bg-[#ef334f]/8"}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0"><p className="text-xs font-bold uppercase tracking-wide text-muted-foreground">{direction}</p><p className="mt-2 break-words text-2xl font-bold tabular-nums">{money(transaction.amount, language)}</p></div>
            <Badge variant="outline" className="shrink-0 font-mono text-base">{transaction.uniqueCode || "—"}</Badge>
          </div>
        </section>

        <DetailGroup icon={<FileText />} title={id ? "Deskripsi" : "Descriptions"}>
          <Detail label={id ? "Bawaan mutasi / transaksi" : "Original transaction description"} value={transaction.subject} wide />
          <Detail label={id ? "Versi EventKita" : "EventKita description"} value={transaction.internalDescription} wide />
        </DetailGroup>

        <DetailGroup icon={<Landmark />} title={id ? "Informasi transaksi" : "Transaction information"}>
          <Detail label={id ? "Tanggal" : "Date"} value={transaction.date} />
          <Detail label={id ? "Waktu" : "Time"} value={transactionTime(transaction)} />
          <Detail label={id ? "No. referensi" : "Reference no."} value={transaction.paymentRef} mono />
          <Detail label="FT Number" value={transaction.ftNumber} mono />
          <Detail label={id ? "Debet/Kredit" : "Debit/Credit"} value={direction} />
          <Detail label={id ? "Saldo riil" : "Running balance"} value={transaction.balance === undefined ? "" : money(transaction.balance, language)} />
          <Detail label={id ? "Jenis transfer" : "Transfer type"} value={transaction.transferType} />
        </DetailGroup>

        <DetailGroup icon={<UserRound />} title={id ? "Pengirim dan penerima" : "Sender and recipient"}>
          <Detail label={id ? "Nama pengirim" : "Sender name"} value={transaction.sourceName} />
          <Detail label={id ? "Bank pengirim" : "Sender bank"} value={transaction.sourceBank} />
          <Detail label={id ? "Rekening pengirim" : "Sender account"} value={transaction.sourceAccount} mono />
          <Detail label={id ? "Nama penerima" : "Recipient name"} value={transaction.beneficiaryName} />
          <Detail label={id ? "Bank penerima" : "Recipient bank"} value={transaction.beneficiaryBank} />
          <Detail label={id ? "Rekening penerima" : "Recipient account"} value={transaction.beneficiaryAccount} mono />
        </DetailGroup>

        <DetailGroup icon={<Tags />} title={id ? "Klasifikasi" : "Classification"}>
          <Detail label="Brand" value={transactionBrandName(transaction, language, units)} />
          <Detail label={id ? "Subkategori" : "Subcategory"} value={transaction.subcategory ? categoryName(transaction.subcategory, language, categories) : "—"} />
          <Detail label={id ? "Kategori" : "Category"} value={categories ? categoryName(transaction.category, language, categories) : transaction.category} />
          <div className="sm:col-span-2"><SimpleProfitInfo row={transaction} language={language}/><details className="mt-2"><summary className="cursor-pointer text-xs font-semibold">{id ? "Akun laporan lanjutan" : "Advanced report account"}</summary><Detail label={id ? "Akun laporan" : "Report account"} value={financial ? accountName(financial, language) : transaction.financialAccount} /><FinancialMappingInfo row={transaction} language={language}/></details></div>
          <Detail label={id ? "Nama rekening" : "Account name"} value={transaction.accountLabel} />
        </DetailGroup>

        <DetailGroup icon={<Archive />} title={id ? "Sumber dan identitas" : "Source and identity"}>
          <Detail label={id ? "Sumber data" : "Data source"} value={sources} wide />
          <Detail label={id ? "Cara input" : "Entry method"} value={transaction.entryMethod === "manual" ? (id ? "Input manual" : "Manual input") : (id ? "Impor file" : "File import")} />
          <Detail label={id ? "Baris sumber" : "Source row"} value={transaction.sourceRowNumber} />
          <Detail label={id ? "ID transaksi" : "Transaction ID"} value={persisted ? transaction.id : ""} mono wide />
        </DetailGroup>

        {(transaction.missingFields.length > 0 || transaction.confirmedEmptyFields.length > 0) && <section className="rounded border border-amber-500/30 bg-amber-500/5 p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold"><AlertTriangle className="size-4 text-amber-600" />{id ? "Status kelengkapan data" : "Data completeness"}</h3>
          {transaction.missingFields.length > 0 && <p className="mt-2 text-xs text-muted-foreground">{id ? "Belum tersedia" : "Unavailable"}: {transaction.missingFields.join(", ")}</p>}
          {transaction.confirmedEmptyFields.length > 0 && <p className="mt-1 text-xs text-muted-foreground">{id ? "Dikonfirmasi sengaja kosong" : "Confirmed intentionally empty"}: {transaction.confirmedEmptyFields.join(", ")}</p>}
        </section>}

        {transaction.originalPayload && Object.keys(transaction.originalPayload).length > 0 && <details className="rounded border bg-muted/15 p-4">
          <summary className="cursor-pointer text-sm font-semibold">{id ? "Payload sumber asli" : "Original source payload"}</summary>
          <pre className="mt-3 max-h-72 overflow-auto rounded bg-muted p-3 text-[10px] leading-5">{JSON.stringify(transaction.originalPayload, null, 2)}</pre>
        </details>}
      </div>
    </SheetContent>
  </Sheet>;
}

function DetailGroup({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return <section className="overflow-hidden rounded border"><h3 className="flex items-center gap-2 border-b bg-muted/35 px-4 py-3 text-sm font-bold text-[#82305b] dark:text-[#f1b9d5]"><span className="[&_svg]:size-4">{icon}</span>{title}</h3><div className="grid gap-px bg-border sm:grid-cols-2">{children}</div></section>;
}

function Detail({ label, value, mono = false, wide = false }: { label: string; value: unknown; mono?: boolean; wide?: boolean }) {
  return <div className={`min-w-0 bg-background p-3 ${wide ? "sm:col-span-2" : ""}`}><p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">{label}</p><p className={`mt-1.5 break-words text-sm ${mono ? "font-mono" : "font-medium"}`}>{available(value)}</p></div>;
}
